import React, { useState, useEffect } from 'react';
import { Outlet, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navbar, Dropdown } from 'flowbite-react';
import { Sun01Icon, Moon02Icon, Logout02Icon } from 'hugeicons-react';
import CustomAvatar from './CustomAvatar';
import { useDarkMode } from '../contexts/DarkModeContext';

const ClockLayout = () => {
  const { currentUser, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 0);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getUserInitials = () => {
    if (currentUser?.displayName) {
      return currentUser.displayName.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    if (currentUser?.email) {
      return currentUser.email[0].toUpperCase();
    }
    return '?';
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}>
      <Navbar
        fluid
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 dark:bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800'
            : 'bg-transparent border-transparent'
        }`}
      >
        <div className="container mx-auto px-6">
          <div className="w-full flex justify-between items-center py-4">
            <Navbar.Brand
              as={Link}
              to="/adhan-clock"
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full transition-all duration-300 ${
                scrolled
                  ? 'hover:bg-gray-100 dark:hover:bg-[#111111]'
                  : isDarkMode
                    ? 'bg-[#111111] hover:bg-[#1A1A1A] shadow-sm'
                    : 'bg-white hover:bg-gray-50 shadow-sm'
              }`}
            >
              <img
                src={isDarkMode ? '/images/logo_white.png' : '/images/logo_green.png'}
                className="h-8 transition-opacity duration-300"
                alt="Ummahti"
              />
            </Navbar.Brand>

            {currentUser && (
              <Dropdown
                arrowIcon={false}
                inline
                label={
                  <div className={`flex items-center gap-4 cursor-pointer rounded-full transition-all duration-300 h-11 ${
                    scrolled
                      ? 'hover:bg-gray-100 dark:hover:bg-[#111111]'
                      : isDarkMode
                        ? 'bg-[#111111] hover:bg-[#1A1A1A] shadow-sm'
                        : 'bg-white hover:bg-gray-50 shadow-sm'
                  }`}>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleDarkMode();
                      }}
                      className={`h-full px-4 rounded-full transition-colors flex items-center justify-center ${
                        isDarkMode ? 'hover:bg-[#1A1A1A]' : 'hover:bg-gray-100'
                      }`}
                    >
                      {isDarkMode ? (
                        <Sun01Icon size={20} className="text-emerald-400 hover:text-emerald-300 transition-colors" />
                      ) : (
                        <Moon02Icon size={20} className="text-gray-700 hover:text-gray-900 transition-colors" />
                      )}
                    </button>
                    <div className="pr-4 flex items-center">
                      <CustomAvatar
                        src={currentUser.photoURL}
                        alt={currentUser.displayName || 'User'}
                        initials={getUserInitials()}
                      />
                    </div>
                  </div>
                }
                className="!p-0 [&>div]:!p-0 [&>div]:!bg-transparent [&>div]:!border-0 [&>div]:!rounded-none [&>div]:!m-0 [&>div>ul]:!py-0"
              >
                <div className={`rounded-[24px] overflow-hidden border transition-all duration-200 min-w-[240px] ${
                  isDarkMode
                    ? 'bg-[#111111] border-gray-800 shadow-lg shadow-black/5'
                    : 'bg-white border-gray-100 shadow-sm'
                }`}>
                  <Dropdown.Header className={`p-4 border-b ${isDarkMode ? 'bg-[#111111] text-white border-gray-800' : 'border-gray-100'}`}>
                    <span className="block text-sm">{currentUser.displayName || currentUser.email}</span>
                    <span className={`block truncate text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {currentUser.email}
                    </span>
                  </Dropdown.Header>
                  <ul className="p-2">
                    <li>
                      <Dropdown.Item
                        onClick={logout}
                        className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                          isDarkMode
                            ? 'text-red-400 hover:bg-red-500/10'
                            : 'text-red-600 hover:bg-red-50'
                        }`}
                      >
                        <Logout02Icon size={18} className="current-color" />
                        Sign out
                      </Dropdown.Item>
                    </li>
                  </ul>
                </div>
              </Dropdown>
            )}
          </div>
        </div>
      </Navbar>

      <main className="relative z-10 flex-1">
        <Outlet />
      </main>
    </div>
  );
};

export default ClockLayout;
